// Escalas de cor do mapa da Visão Geral Agro.
// - Config OFICIAL (ADENDO-4): quando a rota mapa/variaveis devolve `config`
//   (dim do BH Sequencial), ela tem precedência — escala e heatmap idênticos
//   nas duas telas, com manutenção por SQL.
// - Fallbacks retrocompatíveis: ARM placeholder de 5 faixas; demais variáveis
//   escala PROVISÓRIA min–max neutra (DECISÃO-7 do contrato agro-overview.md).
import { AgroOverviewHeatmapConfig } from 'types/Meteorology/Agro/Overview/Dashboard';

export interface ColorScaleStop {
  min: number;
  max: number;
  color: string;
  label?: string;
}

export interface VariableColorScale {
  stops: ColorScaleStop[];
  colorFor: (valor: number) => string;
}

// tema: dados-inicio
export const armColorScale = [
  { max: 20, color: '#e63946', label: 'Crítico' },
  { max: 40, color: '#f4a261', label: 'Baixo' },
  { max: 60, color: '#e9c46a', label: 'Moderado' },
  { max: 80, color: '#52b788', label: 'Adequado' },
  { max: 100, color: '#1d3557', label: 'Ótimo' },
] as const;

export function colorForArmValue(valor: number): string {
  const ultimo = armColorScale[armColorScale.length - 1];
  const faixa = armColorScale.find((f) => valor <= f.max) ?? ultimo;
  return faixa.color;
}

/** Cor neutra para bolinha/badge quando o valor é null (DECISÃO-9). */
export const NEUTRAL_COLOR = '#adb5bd';

const ARM_SCALE: VariableColorScale = {
  stops: armColorScale.map((f, i) => ({
    min: i === 0 ? 0 : armColorScale[i - 1].max,
    max: f.max,
    color: f.color,
    label: f.label,
  })),
  colorFor: colorForArmValue,
};

// Rampa sequencial neutra (claro → escuro), sem rótulos semânticos.
const NEUTRAL_RAMP = ['#dee5ec', '#b8c9d9', '#8fa9c2', '#6288a9', '#3d6a8f'];
// tema: dados-fim

export function neutralMinMaxColorScale(valores: number[]): VariableColorScale {
  if (valores.length === 0) {
    return { stops: [], colorFor: () => NEUTRAL_RAMP[0] };
  }
  const min = valores.reduce((acc, v) => Math.min(acc, v), valores[0]);
  const max = valores.reduce((acc, v) => Math.max(acc, v), valores[0]);
  const span = max - min;
  const stops: ColorScaleStop[] = NEUTRAL_RAMP.map((color, i) => ({
    min: min + (span * i) / NEUTRAL_RAMP.length,
    max: min + (span * (i + 1)) / NEUTRAL_RAMP.length,
    color,
  }));
  return {
    stops,
    colorFor: (valor: number) => {
      const stop = stops.find((s) => valor <= s.max) ?? stops[stops.length - 1];
      return stop.color;
    },
  };
}

/** Escala discreta (escala-bar + modal ⓘ) a partir dos legendRanges oficiais. */
export function scaleFromConfig(config: AgroOverviewHeatmapConfig): VariableColorScale {
  const stops: ColorScaleStop[] = (config.legendRanges ?? []).map((r) => ({
    min: r.min,
    max: r.max ?? Number.POSITIVE_INFINITY,
    color: r.color,
  }));
  return {
    stops,
    colorFor: (valor: number) => {
      const stop = stops.find((s) => valor <= s.max) ?? stops[stops.length - 1];
      return stop?.color ?? NEUTRAL_COLOR;
    },
  };
}

export function colorScaleForVariable(
  variavel: string,
  valores: number[],
  config?: AgroOverviewHeatmapConfig | null,
): VariableColorScale {
  if (config?.legendRanges?.length) return scaleFromConfig(config);
  return variavel === 'arm' ? ARM_SCALE : neutralMinMaxColorScale(valores);
}

/**
 * Config de heatmap PROVISÓRIA para variáveis sem faixas oficiais: rampa
 * neutra sobre o domínio real dos dados (fixedMin permite negativos, caso
 * saldo_bh). Null sem dados — a camada simplesmente não renderiza.
 */
export function derivedNeutralHeatConfig(
  valores: number[],
): AgroOverviewHeatmapConfig | null {
  if (valores.length === 0) return null;
  const min = valores.reduce((acc, v) => Math.min(acc, v), valores[0]);
  const max = valores.reduce((acc, v) => Math.max(acc, v), valores[0]);
  const gradient: Record<string, string> = {};
  NEUTRAL_RAMP.forEach((color, i) => {
    gradient[((i + 1) / NEUTRAL_RAMP.length).toFixed(3)] = color;
  });
  return {
    gradient,
    minOpacity: 0.3,
    maxOpacity: 0.7,
    fixedMin: min,
    fixedMax: max,
  };
}

/** Formata um limite de faixa para exibição na escala/legenda. */
export function formatScaleValue(valor: number): string {
  return Number.isInteger(valor) ? String(valor) : valor.toFixed(1);
}

/**
 * Rótulo do segmento na barra de escala: o teto da faixa. A última faixa
 * oficial pode vir aberta (`max: null` → infinito); mostra ">min".
 */
export function scaleStopLabel(stop: ColorScaleStop): string {
  return Number.isFinite(stop.max)
    ? formatScaleValue(stop.max)
    : `>${formatScaleValue(stop.min)}`;
}

/** Faixa completa com unidade (modal ⓘ): "0–20%", "0–1 dias", "> 80%". */
export function scaleRangeLabel(min: number, max: number, unidade: string | null): string {
  const range = Number.isFinite(max)
    ? `${formatScaleValue(min)}–${formatScaleValue(max)}`
    : `> ${formatScaleValue(min)}`;
  if (unidade === '%') return `${range}%`;
  return unidade ? `${range} ${unidade}` : range;
}

// Texto sobre a cor da faixa: as cores são de dado (iguais nos dois temas),
// então o texto também é fixo — branco ou o navy do claro, o que contrastar mais.
// Luminância de #17324E, o texto de .on-light-scale-color. tema: fixo
const SCALE_TEXT_DARK_LUMINANCE = 0.0301;

function relativeLuminance(hex: string): number | null {
  const m = hex.trim().match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (!m) return null;
  const digits = m[1].length === 3 ? m[1].replace(/./g, '$&$&') : m[1];
  const [r, g, b] = [0, 2, 4].map((i) => {
    const c = parseInt(digits.slice(i, i + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * true quando o texto escuro contrasta mais que o branco sobre a cor
 * (contraste WCAG). Cor que não é hex cai no texto branco.
 */
export function isLightScaleColor(color: string): boolean {
  const l = relativeLuminance(color);
  if (l === null) return false;
  const contrasteBranco = 1.05 / (l + 0.05);
  const contrasteEscuro = (l + 0.05) / (SCALE_TEXT_DARK_LUMINANCE + 0.05);
  return contrasteEscuro > contrasteBranco;
}
